import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, CONTACT } from "../data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_30px_rgba(0,0,0,0.04)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img src={solid ? "/logo.png?v=3" : "/logo-white.png?v=3"} alt="J&S Air Conditioning" className="h-9 md:h-10 w-auto transition-transform duration-300 group-hover:scale-[1.03]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => {
            const active = n.to === pathname;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`relative px-3.5 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                  solid ? "text-js-charcoal/80 hover:text-js-blue" : "text-white/90 hover:text-white"
                }`}
              >
                {n.label}
                {active && <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-0.5 bg-js-red rounded-full" />}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={CONTACT.phoneHref} data-testid="nav-call" className={`flex items-center gap-2 text-sm font-semibold ${solid ? "text-js-blue" : "text-white"}`}>
            <Phone size={16} /> {CONTACT.phone}
          </a>
          <Link to="/quote" data-testid="nav-quote" className="px-5 py-2.5 rounded-full bg-js-blue text-white text-sm font-semibold hover:bg-js-dark transition-colors duration-300">
            Request Quote
          </Link>
        </div>

        <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className={`lg:hidden p-2 ${solid ? "text-js-charcoal" : "text-white"}`}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-black/5"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="py-3 text-js-charcoal font-medium border-b border-black/5">
                  {n.label}
                </Link>
              ))}
              <Link to="/quote" data-testid="mobile-nav-quote" className="mt-4 text-center px-5 py-3 rounded-full bg-js-blue text-white font-semibold">
                Request Quote
              </Link>
              <a href={CONTACT.phoneHref} className="mt-3 text-center text-js-blue font-semibold">{CONTACT.phone}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
